// SingleDocUpload.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';

function SingleDocUpload({ onClose, onUploadSuccess, isModal = false }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [lessonNumber, setLessonNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [isError, setIsError] = useState(false);

  // Use unique IDs for inputs if in modal to avoid conflicts
  const lessonNumberInputId = isModal ? 'lessonNumber-modal' : 'lessonNumber-page';
  const fileInputId = isModal ? 'file-input-modal' : 'file-input-page';

  useEffect(() => {
    // Attempt to clear the file input visually when selectedFile is reset
    if (!selectedFile && document.getElementById(fileInputId)) {
      document.getElementById(fileInputId).value = '';
    }
  }, [selectedFile, fileInputId]); // Add fileInputId to dependencies

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    } else {
      setSelectedFile(null);
    }
    setUploadMessage('');
    setIsError(false);
  };

  const handleLessonNumberChange = (event) => {
    setLessonNumber(event.target.value);
    setUploadMessage('');
    setIsError(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedFile || !lessonNumber.trim()) {
      setUploadMessage('Please select a file and enter a lesson number.');
      setIsError(true);
      return;
    }

    setIsLoading(true);
    setUploadMessage('');
    setIsError(false);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('lessonNumber', lessonNumber.trim());


    
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log("🚀 ~ handleSubmit ~ response:", response)
      setUploadMessage(response.data.message || 'File uploaded successfully!');
      setIsError(false);
      setSelectedFile(null); // Triggers useEffect to clear file input
      setLessonNumber('');
      
      if (onUploadSuccess) {
        onUploadSuccess(); // Notify parent
      }
      // Optionally auto-close modal after a short delay
      if (isModal && onClose) {
        setTimeout(() => {
            onClose();
        }, 1500); // Close after 1.5 seconds so user can see message
      }

    } catch (error) {
      let errorMessage = 'An error occurred during upload.';
    if (error.response?.data?.error) {
  errorMessage = error.response.data.error;
} else if (error.response?.data?.message) {
  errorMessage = error.response.data.message;
} else if (error.message) {
  errorMessage = error.message;
}
      setUploadMessage(errorMessage);
      setIsError(true);
      console.error('Upload error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const wrapperClass = isModal
    ? "font-sans" // Modal content doesn't need its own bg or min-height
    : "p-5 md:p-8 font-sans min-h-screen bg-gray-100";

  const formContainerClass = isModal
    ? "p-6 md:p-8" // Padding for modal content
    : "max-w-xl mx-auto bg-white p-6 md:p-8 rounded-lg shadow-xl";

  return (
    <div className={wrapperClass}>
      {!isModal && (
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-6 border-b-2 border-blue-600 pb-3">
          Upload Document
        </h2>
      )}

      {isModal && (
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Upload New Document</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Add a new lesson document to your library</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg p-2 transition-colors duration-200"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
          </button>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className={formContainerClass}
      >
        <div className="space-y-6">
          <div>
            <label
              htmlFor={lessonNumberInputId}
              className="block text-sm font-semibold text-gray-900 dark:text-white mb-2"
            >
              Lesson Number
            </label>
            <input
              type="text"
              id={lessonNumberInputId}
              value={lessonNumber}
              onChange={handleLessonNumberChange}
              placeholder="Enter the lesson number (e.g., 1, 2, 3...)"
              className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base transition-all duration-200 text-gray-900 dark:text-gray-100"
              required
            />
          </div>

          <div>
            <label
              htmlFor={fileInputId}
              className="block text-sm font-semibold text-gray-900 dark:text-white mb-2"
            >
              Select Document
            </label>
            <div className="relative">
              <input
                type="file"
                id={fileInputId}
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl cursor-pointer file:mr-4 file:py-3 file:px-4 file:rounded-l-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 dark:file:bg-blue-900/20 file:text-blue-700 dark:file:text-blue-300 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                required
              />
              {selectedFile && (
                <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium text-green-800 dark:text-green-200">
                      Selected: {selectedFile.name}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={`flex ${isModal ? 'justify-end space-x-3' : 'justify-start'} mt-8`}>
          {isModal && (
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading || !selectedFile || !lessonNumber.trim()}
            className={`px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${isModal ? '' : 'w-full'}`}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Uploading...
              </div>
            ) : (
              'Upload Document'
            )}
          </button>
        </div>

        {uploadMessage && (
          <div
            className={`mt-6 p-4 rounded-xl border ${
              isError
                ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800'
                : 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border-green-200 dark:border-green-800'
            }`}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {isError ? (
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">{uploadMessage}</p>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

export default SingleDocUpload;